import {AxiosProgressEvent, isCancel} from 'axios';
import bytes from 'bytes';
import {isEqual, isNumber} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionHistoryApi} from 'api';
import {GlobalLoader, Button, notifier, Icon, Tooltip, DataWrapper} from 'components';
import {usePreventWindowUnload, usePrevious, useTask} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

import {REQUEST_SESSION_HISTORY_DATE_FORMAT} from '../../constants';
import {ActivityFilters} from '../../types';

type Props = {
    disabled: boolean;
    filters: ActivityFilters;
};

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.SESSION_HISTORY.DOWNLOAD_FILES;

export const DownloadFiles: React.FC<Props> = ({disabled, filters}: Props) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [totalSize, setTotalSize] = useState(0);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [sizeLoading, setSizeLoading] = useState(false);
    const prevFilters = usePrevious(filters);

    const abortController = useMemo(() => new AbortController(), []);

    // TODO: add check on component unmount instead
    usePreventWindowUnload(loading);

    useEffect(() => () => {
        abortController.abort();
    }, [abortController]);

    const {startTask} = useTask(sessionHistoryApi.getAfterSessionFilesSize, {
        onComplete: (data: {size: number}) => setTotalSize(data?.size),
        onToggleLoading: setSizeLoading,
        onError: () => notifier.error({details: t(T_KEYS.UNEXPECTED_ERROR)})
    });

    const refreshFilesSize = useCallback(() => {
        if (sizeLoading) {
            return;
        }
        setTotalSize(null);
        const startDate = filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT);
        const endDate = filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT);
        startTask({...filters, startDate, endDate});
    }, [sizeLoading, filters, startTask]);

    useEffect(() => {
        if (isEqual(prevFilters, filters)) {
            setTotalSize(null);
            return;
        }
    }, [filters, prevFilters]);

    const downloadAfterSessionFiles = useCallback(async () => {
        setLoading(true);
        setLoadingProgress(0);
        try {
            const startDate = filters.startDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT);
            const endDate = filters.endDate.format(REQUEST_SESSION_HISTORY_DATE_FORMAT);
            const formattedFilters = {...filters, startDate, endDate};
            const fileName = `${t(T_KEYS.FILE_NAME_PREFIX)} ${startDate}-${endDate}.zip`;
            await sessionHistoryApi.downloadAfterSessionFiles(formattedFilters, fileName, {
                signal: abortController.signal,
                onDownloadProgress: (event: AxiosProgressEvent) => setLoadingProgress(Math.round(event.loaded / totalSize * 100))
            });
        } catch (error) {
            if (!isCancel(error)) {
                notifier.error({details: t(T_KEYS.UNEXPECTED_ERROR)});
            }
        } finally {
            setLoading(false);
        }
    }, [t, filters, totalSize, abortController]);

    return (
        <Fragment>
            {loading && <GlobalLoader value={loadingProgress} />}
            <div className='tw-flex'>
                <Button
                    icon='arrow-down'
                    width='large'
                    loading={loading}
                    className='tw-mr-2'
                    disabled={disabled}
                    onClick={downloadAfterSessionFiles}
                >
                    {t(T_KEYS.BTN_TITLE)}
                </Button>
                <div className='tw-flex tw-items-center'>
                    {!sizeLoading && isNumber(totalSize) && ` ${bytes(totalSize)}`}
                    <Tooltip header={t(T_KEYS.REFRESH_SIZE_BTN_TITLE)}>
                        <DataWrapper
                            isLoading={sizeLoading}
                            loadingMinHeight={false}
                            loaderAbsolute={false}
                            svgClassName='tw-max-h-5'
                        >
                            <Icon name='refresh' className='tw-ml-2' size='sm' button onClick={refreshFilesSize} />
                        </DataWrapper>
                    </Tooltip>
                </div>
            </div>
        </Fragment>
    );
};
